import MoreVertIcon from '@mui/icons-material/MoreVertTwoTone';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Booking, FullMappedDoor } from '../../api/types';
import { makeAddIssueReportForOrderUrl, makeRentalBookingDetailsUrl, paths } from '../../routes';
import { makeHasPermissionSelector, tenantSelector } from '../../selector/authenticationSelector';
import { setBreadcrumbPathAndBookingNumber } from '../../store/slices/bookingSlice';
import Permission from '../../common/permissions';
import CheckCircleIcon from '../../components/icons/CheckCircleIcon';
import CrossCircleIcon from '../../components/icons/CrossCircleIcon';
import ClockIcon from '../../components/icons/ClockIcon';
import LockClosedIcon from '../../components/icons/LockClosedIcon';
import LockOpenIcon from '../../components/icons/LockOpenIcon';
import DoorBookingsPopover from './DoorBookingsPopover';

interface DoorListItemProps {
    doorNumber: number;
    doorStatus: FullMappedDoor | undefined;
    depotId: string;
    locationId: string;
    enableLockDoor?: boolean;
    enableUnLockDoor?: boolean;
    enableRebookDoor?: boolean;
    onOpenClick: () => void;
    onLockClick?: () => void;
    onUnlockClick?: () => void;
    onRebookDoorClick?: () => void;
    buttonDisabled?: boolean;
    booking?: Booking;
}

const assetStateConfig: Record<string, { icon: React.ReactNode, color: string }> = {
    available: { icon: <CheckCircleIcon className={'w-4 h-4'} />, color: 'text-[#27AE60]' },
    blocked: { icon: <CrossCircleIcon className={'w-4 h-4'} />, color: 'text-[#E72525]' },
    inUse: { icon: <ClockIcon className={'w-4 h-4 text-[#0068FF]'} />, color: 'text-[#0068FF]' },
    soonAvailable: { icon: <ClockIcon className={'w-4 h-4 text-[#F2994A]'} />, color: 'text-[#F2994A]' },
};

const DoorListItem = (props: DoorListItemProps) => {
    const { t } = useTranslation();
    const [menuAnchorEL, setMenuAnchorEl] = useState(null);
    const navigate = useNavigate();
    const tenant = useSelector(tenantSelector);
    const dispatch = useDispatch();
    const location = useLocation();

    const hasCreateIssueReportsPermission = useSelector(
        makeHasPermissionSelector(Permission.createIssueReports()),
    );

    const [bookingsAnchorEl, setBookingsAnchorEl] = useState<HTMLElement | null>(null);

    const handleRedirectToIssueReport = () => {
        if (props.booking && props.booking.items[0].doors && props.booking.items[0].doors[0].door) {
            navigate(makeAddIssueReportForOrderUrl(props.booking.bookingNumber), {
                state: {
                    locationId: props.booking?.locationId,
                    depotId: props.booking?.items[0].depot?.id,
                    door: props.booking?.items[0].doors[0].door,
                    orderId: props.booking?.id,
                    service: (props.booking as any)?.useCase ?? '',
                    bookingNumber: props.booking?.bookingNumber ?? '',
                },
            });
        } else {
            navigate(paths.addIssueReports, {
                state: {
                    locationId: props.locationId,
                    depotId: props.depotId,
                    door: props.doorNumber,
                },
            });
        }
    };

    const openActionMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setMenuAnchorEl(event.currentTarget as any);
    };

    const closeActionMenu = () => {
        setMenuAnchorEl(null);
    };

    /**
     * Open a booking of this Fach. Reached from the "Buchungen" popover — for
     * the booking currently holding the locker as well as for a past one, so
     * the old menu entry that only worked while the door was rebookable is
     * gone and every row behaves the same.
     */
    const onOpenBooking = (booking: { id: string, bookingNumber: string }) => {
        if (!booking.id || !booking.bookingNumber) return;
        dispatch(
            setBreadcrumbPathAndBookingNumber({
                bookingNumber: booking.bookingNumber,
                path: location.pathname,
            }),
        );
        if (tenant !== 'segmenta') {
            navigate(makeRentalBookingDetailsUrl(booking.id));
        } else {
            navigate(`/bookings/search/${booking.bookingNumber}`);
        }
    };

    const enableLockDoorFunctionality = Boolean(props.enableLockDoor && props.doorStatus);
    const isSoonAvailable = props.doorStatus?.assetState === 'soonAvailable';
    const isInUse = props.doorStatus?.assetState === 'inUse';
    const enableUnlockDoor = props.doorStatus?.assetState === 'blocked';
    // A locker can be taken out of service while a customer still has it —
    // equipment reported broken mid-booking. The running rental is untouched
    // (the customer keeps this door until their time is up); the locker just
    // never goes back into rotation when they return. Waiting for the return
    // to block it is exactly the moment that used to get missed.
    // See docs/plans/blocking-lockers-in-use.md.
    const enableLockDoor =
        props.doorStatus?.assetState === 'available' || isSoonAvailable || isInUse;
    const showActionMenu = enableLockDoorFunctionality || props.enableRebookDoor;

    function showActionButton() {
        const hasLockOrUnlockPermission = enableLockDoorFunctionality || props.enableRebookDoor;
        return hasLockOrUnlockPermission && (enableLockDoor || enableUnlockDoor);
    }

    const assetState = props.doorStatus?.assetState ?? 'blocked';
    const stateConfig = assetStateConfig[assetState] ?? assetStateConfig.blocked;

    // The unit's own name: a Group's language title is not the unit's
    // (product refactor phase 2b, `bookedDisplay` in `@kolula/shared`).
    const assetDisplayName = props.doorStatus?.assetName ?? t('common.no_asset');
    const lockerSize = props.booking?.lockerSize;
    const isRentalProduct = lockerSize && assetDisplayName !== lockerSize;

    return (
        <div className={'bg-text-contrast rounded-[5px] shadow-sm p-4'}>
            <div className={'flex items-start justify-between gap-2'}>
                <div className={'min-w-0'}>
                    <h3 className={'font-semibold text-sm text-foreground'}>
                        {t('common.locker')} {props.doorStatus?.door}
                        <span className={'font-normal text-foreground/40'}>{' \u2014 '}</span>
                        <span className={'font-normal'}>
                            {lockerSize
                                ? <>{t('common.size')} {lockerSize}{isRentalProduct && ` \u2014 ${assetDisplayName}`}</>
                                : assetDisplayName
                            }
                        </span>
                    </h3>
                    {props.booking?.useCase && (
                        <p className={'text-xs text-foreground/60'}>
                            {t(`service_type_selector.${props.booking.useCase}`, { defaultValue: props.booking.useCase })}
                        </p>
                    )}
                    {props.doorStatus?.assetAccessories && (
                        <p className={'text-xs text-foreground/60'}>
                            ({props.doorStatus.assetAccessories.join(', ')})
                        </p>
                    )}
                    {props.booking?.contactData?.email && (
                        <p className={'text-xs text-foreground/60'}>
                            {props.booking.contactData.email}
                        </p>
                    )}
                </div>
                <div className={'flex items-center gap-1 shrink-0'}>
                    {/* Reading the locker's history is the safe action next to
                        the door-opening one, so it sits on the card rather
                        than in the menu of write actions — but outlined, so
                        the two are not one mis-click apart. */}
                    <button
                        className={
                            'text-sm text-primary border border-primary rounded px-3 py-1 ' +
                            'hover:bg-primary/5 transition-colors'
                        }
                        onClick={(event) => setBookingsAnchorEl(event.currentTarget)}
                    >
                        {t('depot_details.door_bookings.button')}
                    </button>
                    <button
                        className={
                            'text-sm text-white bg-primary rounded px-3 py-1 ' +
                            'hover:opacity-90 transition-opacity'
                        }
                        onClick={props.onOpenClick}
                    >
                        {t('common.open')}
                    </button>
                    {showActionMenu && (
                        <>
                            <IconButton
                                className={showActionButton() ? 'text-foreground' : 'text-gray-400'}
                                onClick={openActionMenu}
                                size={'small'}
                            >
                                <MoreVertIcon fontSize={'small'} />
                            </IconButton>
                            <Menu
                                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                                open={Boolean(menuAnchorEL)}
                                anchorEl={menuAnchorEL}
                                onClose={closeActionMenu}
                                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            >
                                {hasCreateIssueReportsPermission && (
                                    <MenuItem onClick={handleRedirectToIssueReport}>
                                        {t('bookings.report_issue')}
                                    </MenuItem>
                                )}
                                {props.onRebookDoorClick && (
                                    <MenuItem onClick={props.onRebookDoorClick}>
                                        {t('bookings.block_and_change')}
                                    </MenuItem>
                                )}
                                {props.doorStatus!.assetState === 'blocked' && (
                                    <MenuItem onClick={props.onUnlockClick}>
                                        {t('common.unblock')}
                                    </MenuItem>
                                )}
                                {(props.doorStatus!.assetState === 'available' || isInUse) && (
                                    <MenuItem onClick={props.onLockClick}>
                                        {t('common.block')}
                                    </MenuItem>
                                )}
                            </Menu>
                        </>
                    )}
                </div>
            </div>
            <div className={'flex items-center gap-3 mt-2'}>
                <Tooltip title={t('location_details.resource_status.' + assetState)}>
                    <span className={'flex items-center gap-1 ' + stateConfig.color}>
                        {stateConfig.icon}
                        <span className={'text-xs'}>
                            {t('location_details.resource_status.' + assetState)}
                        </span>
                    </span>
                </Tooltip>
                {props.doorStatus?.doorState === 'open' && (
                    <Tooltip title={t('location_details.door_status.open')}>
                        <span className={'flex items-center gap-1 text-[#E72525]'}>
                            <LockOpenIcon className={'w-4 h-4'} />
                            <span className={'text-xs'}>
                                {t('location_details.door_status.open')}
                            </span>
                        </span>
                    </Tooltip>
                )}
                {props.doorStatus?.doorState === 'closed' && (
                    <Tooltip title={t('location_details.door_status.closed')}>
                        <span className={'flex items-center gap-1 text-gray-400'}>
                            <LockClosedIcon className={'w-4 h-4'} />
                            <span className={'text-xs'}>
                                {t('location_details.door_status.closed')}
                            </span>
                        </span>
                    </Tooltip>
                )}
            </div>
            {/* Mounted only while open, so the history refetches each time the
                popover is opened rather than serving a cached list. */}
            {bookingsAnchorEl && (
                <DoorBookingsPopover
                    open={true}
                    anchorEl={bookingsAnchorEl}
                    onClose={() => setBookingsAnchorEl(null)}
                    depotId={props.depotId}
                    locationId={props.locationId}
                    door={props.doorStatus?.door ?? props.doorNumber}
                    currentOrderId={props.booking?.id}
                    onSelectBooking={(booking) => {
                        setBookingsAnchorEl(null);
                        onOpenBooking({
                            id: booking.orderId,
                            bookingNumber: booking.bookingNumber,
                        });
                    }}
                />
            )}
        </div>
    );
};

export default DoorListItem;
